/**
 * Labstep
 *
 * @module grid/Index/coldefs/metadatas
 * @desc Column Definition for metadatas
 */

import { FilterMetadata } from 'labstep-web/components/Filter/Metadata';
import { PostFilterMetadatasSupportedTypes } from 'labstep-web/components/Filter/Metadata/Form/fields';
import type { FilterMetadataType } from 'labstep-web/components/Filter/Metadata/Form/types';
import type { FilterMetadataProps } from 'labstep-web/components/Filter/Metadata/types';
import { MetadataActionDelete } from 'labstep-web/components/Metadata/Action/Delete';
import { MetadataActionEdit } from 'labstep-web/components/Metadata/Action/Edit';
import { ICONS } from 'labstep-web/constants/icons';
import type {
  ColDef,
  ColDefParams,
  ColGroupDef,
} from 'labstep-web/core/DataGrid/types';
import { getEntityDefault } from 'labstep-web/core/DataGrid/utils';
import colDefValue from 'labstep-web/grid/Index/coldefs/Metadata/value';
import colDefMolecule from 'labstep-web/grid/Index/coldefs/Molecule';
import type { GridIndexContextMenuFilterProps } from 'labstep-web/grid/Index/components/ContextMenu/types';
import type { Metadata } from 'labstep-web/models/metadata';
import {
  type MetadataParent,
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import { colDefMetadataEntityRelation } from './entityRelation';

export const COLUMN_WIDTH_METADATA = 200;

/**
 * Using the template metadata (column) either return the corresponding metadata from
 * resource_item metadata thread or if it doesn't exist return the template metadata.
 * @param metadataTemplate Metadata template
 * @returns Function on metadata or template metadata
 */
export const getNestedMetadata =
  <T extends MetadataParent>(
    metadataTemplate: Metadata,
    getNestedParentEntity?: (
      params: ColDefParams<T>,
    ) => MetadataParent,
  ) =>
  (params: ColDefParams<T>): Metadata | undefined => {
    const getParentEntity = getNestedParentEntity || getEntityDefault;
    const parentEntity = getParentEntity(params);
    if (!parentEntity) {
      return undefined;
    }
    const metadata = parentEntity.metadata_thread?.metadatas?.find(
      (m) => m.template_id === metadataTemplate.id,
    );
    return metadata || metadataTemplate;
  };

/**
 * For all metadatas on resource template, return a column
 * @param entityName Entity name
 * @param template Template
 * @param getNestedParentEntity Custom fn to get parent entity as nested field
 * @returns Column definitions
 */
export const getMetadataColDefs = <T extends MetadataParent>(
  entityName: MetadataParent['entityName'],
  template: MetadataParent,
  getNestedParentEntity?: (params: ColDefParams<T>) => MetadataParent,
  filterProps?: Pick<
    FilterMetadataProps,
    'entityName' | 'childEntityName'
  >,
): ColDef<T>[] => {
  if (!template || !template.metadata_thread) {
    return [];
  }
  const { metadatas } = template.metadata_thread;
  if (metadatas) {
    const colDefs: ColDef<T>[] = [];
    metadatas.forEach((metadataTemplate) => {
      const columnProps = {
        colId: `metadata:${metadataTemplate.guid}`,
        headerName: metadataTemplate.label || undefined,
        headerComponentParams: {
          icon: ICONS.metadata.secondary,
          cornerIcon: ICONS[entityName].primary,
          firstItems: (
            <MetadataActionEdit
              entity={template}
              metadata={metadataTemplate}
              actionComponentProps={{
                type: 'option',
                icon: 'cog',
                text: 'Configure field',
              }}
            />
          ),
          lastItems: (
            <MetadataActionDelete
              metadata={metadataTemplate}
              entity={template}
            />
          ),
          filter:
            filterProps &&
            PostFilterMetadatasSupportedTypes.includes(
              metadataTemplate.type,
            )
              ? ({
                  addPostFilter,
                  ...rest
                }: GridIndexContextMenuFilterProps) =>
                  addPostFilter ? (
                    <FilterMetadata
                      defaultValues={{
                        label: metadataTemplate.label,
                        type: {
                          value:
                            metadataTemplate.type as FilterMetadataType,
                          label:
                            MetadataTypeValues[metadataTemplate.type],
                        },
                      }}
                      {...filterProps}
                      {...rest}
                      addPostFilter={addPostFilter}
                    />
                  ) : null
              : undefined,
        },
        width: COLUMN_WIDTH_METADATA,
      };
      const getNestedEntity = getNestedMetadata(
        metadataTemplate,
        getNestedParentEntity,
      );

      if (
        metadataTemplate.type === MetadataType.entity_relation &&
        metadataTemplate.field_definition
      ) {
        colDefs.push(
          colDefMetadataEntityRelation(metadataTemplate, entityName),
        );
      } else if (metadataTemplate.type === MetadataType.molecule) {
        const moleculeColDefs = colDefMetadataMolecule(
          entityName,
          getNestedEntity,
          getNestedParentEntity,
        ).children;
        moleculeColDefs.forEach((colDef) =>
          colDefs.push({
            ...colDef,
            colId: `${columnProps.colId}_molecule_${colDef.colId}`,
          }),
        );
      } else {
        colDefs.push(
          colDefValue({
            getNestedEntity,
            columnProps,
            getNestedParentEntity,
          }),
        );
      }
    });
    return colDefs;
  }
  return [];
};

/**
 * Column definition for metadata molecule
 * @param entityName Entity name
 * @param getNestedMetadataEntity Custom fn to get metadata as nested field
 * @param getNestedParentEntity Custom fn to get parent entity as nested field
 * @returns Column definition
 */
export const colDefMetadataMolecule = <T extends MetadataParent>(
  entityName: T['entityName'],
  getNestedMetadataEntity: (
    params: ColDefParams<T>,
  ) => Metadata | undefined,
  getNestedParentEntity?: (params: ColDefParams<T>) => MetadataParent,
): ColGroupDef<T> => {
  const getParentEntity = getNestedParentEntity || getEntityDefault;
  return colDefMolecule({
    getNestedEntity: (params) =>
      getNestedMetadataEntity(params)?.molecule,
    getCreateProps: (params) =>
      getNestedMetadataEntity(params)?.getCreateProps(
        getParentEntity(params),
      ),
    columnProps: {
      headerComponentParams: {
        icon: ICONS.metadata.secondary,
        cornerIcon: ICONS[entityName].primary,
      },
      width: COLUMN_WIDTH_METADATA,
    },
  });
};
